import React, { useEffect, useState } from "react";
import {
  ChevronRight,
  ChevronDown,
  EyeOff,
  Lock,
  Box,
  Type,
  Image,
  Layers,
  Code,
  GripVertical,
  Heading,
  Link,
  MousePointerClick,
  PenTool,
  List,
  ListOrdered,
  ListTree,
  TextCursorInput,
  FormInput,
  AlignLeft,
  Navigation,
  PanelTop,
  PanelBottom,
  LayoutDashboard,
  Sidebar,
  Play,
  Music,
  Link2,
} from "lucide-react";
import {
  DndContext,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  DragOverEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { cn } from "@/lib/utils";

interface LayerElement {
  id: string;
  name: string;
  tagName: string;
  className: string;
  text: string;
  type: string;
  icon: string;
  visible: boolean;
  locked: boolean;
  opacity: number;
  parent: string | null;
  depth: number;
  hasChildren: boolean;
  children: LayerElement[];
  childCount: number;
  collapsed: boolean;
}

interface LayersPanelProps {
  layers: LayerElement[];
  selectedElementPath?: string | null;
  onElementSelect: (elementId: string) => void;
  onElementHover?: (elementId: string | null) => void;
  onReorder?: (from: string, to: string) => void;
  readOnly?: boolean;
}

// Map element types to Lucide icons
const getElementIcon = (tagName: string, _type: string) => {
  const iconProps = { className: "w-4 h-4", strokeWidth: 2 };

  switch (tagName.toLowerCase()) {
    // Structural containers
    case "div":
      return <Box {...iconProps} />;
    case "section":
      return <LayoutDashboard {...iconProps} />;
    case "article":
    case "main":
      return <AlignLeft {...iconProps} />;
    case "aside":
      return <Sidebar {...iconProps} />;
    case "nav":
      return <Navigation {...iconProps} />;
    case "header":
      return <PanelTop {...iconProps} />;
    case "footer":
      return <PanelBottom {...iconProps} />;

    // Headings
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6":
      return <Heading {...iconProps} />;

    // Text
    case "p":
    case "span":
      return <Type {...iconProps} />;

    // Links
    case "a":
      return <Link2 {...iconProps} />;

    // Media
    case "img":
      return <Image {...iconProps} />;
    case "video":
      return <Play {...iconProps} />;
    case "audio":
      return <Music {...iconProps} />;

    // Interactive
    case "button":
      return <MousePointerClick {...iconProps} />;

    // SVG
    case "svg":
    case "path":
    case "circle":
    case "rect":
      return <PenTool {...iconProps} />;

    // Lists
    case "ul":
      return <List {...iconProps} />;
    case "ol":
      return <ListOrdered {...iconProps} />;
    case "li":
      return <ListTree {...iconProps} />;

    // Forms
    case "form":
      return <FormInput {...iconProps} />;
    case "input":
    case "textarea":
      return <TextCursorInput {...iconProps} />;

    // Code
    case "code":
    case "pre":
      return <Code {...iconProps} />;

    default:
      return <Layers {...iconProps} />;
  }
};

const SortableLayerItem: React.FC<{
  layer: LayerElement;
  selectedElementPath?: string | null;
  onElementSelect: (elementId: string) => void;
  onElementHover?: (elementId: string | null) => void;
  onToggleCollapse: (elementId: string) => void;
  overId: string | null;
  readOnly?: boolean;
}> = ({
  layer,
  selectedElementPath,
  onElementSelect,
  onElementHover,
  onToggleCollapse,
  overId,
  readOnly = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: layer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isSelected = selectedElementPath === layer.id;
  const showDropIndicator = overId === layer.id && !isDragging;

  const handleClick = () => {
    onElementSelect(layer.id);
  };

  const handleMouseEnter = () => {
    if (onElementHover) {
      onElementHover(layer.id);
    }
  };

  const handleMouseLeave = () => {
    if (onElementHover) {
      onElementHover(null);
    }
  };

  const handleToggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleCollapse(layer.id);
  };

  return (
    <div ref={setNodeRef} style={style} className="w-full relative ">
      {/* Drop Indicator */}
      {showDropIndicator && (
        <div className="absolute -top-[1px] left-0 right-0 h-[2px] z-20 pointer-events-none bg-primary"></div>
      )}

      <div
        className={cn(
          "flex items-center gap-2 px-3 py-2 hover:bg-accent hover:border hover:border-border rounded transition-colors",
          isSelected && "bg-accent border-l-2 border-primary",
          isDragging && "shadow-lg ring-2 ring-primary",
          isOver && "bg-accent/50",
        )}
        style={{ paddingLeft: `${layer.depth * 16 + 12}px` }}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Drag Handle */}
        {!readOnly ? (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none"
          >
            <GripVertical className="w-4 h-4 text-muted-foreground hover:text-foreground" />
          </div>
        ) : (
          <div className="w-4" />
        )}
        {/* Collapse/Expand Icon */}
        {layer.hasChildren && (
          <button
            onClick={handleToggleCollapse}
            className="w-4 h-4 flex items-center justify-center hover:bg-accent rounded"
          >
            {layer.collapsed ? (
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-3 h-3 text-muted-foreground" />
            )}
          </button>
        )}
        {!layer.hasChildren && <div className="w-4" />}

        {/* Element Icon */}
        <div
          className={`flex items-center justify-center ${
            isSelected ? "text-primary" : "text-muted-foreground"
          }`}
        >
          {getElementIcon(layer.tagName, layer.type)}
        </div>

        {/* Element Name */}
        <span
          className={`flex-1 text-sm truncate ${
            isSelected ? "text-foreground font-medium" : "text-foreground"
          }`}
        >
          {layer.name}
        </span>

        {/* Tag Name */}
        <span className="text-xs text-muted-foreground font-mono">
          {layer.tagName}
        </span>

        {/* Visibility & Lock Icons */}
        <div className="flex items-center gap-1">
          {!layer.visible && (
            <EyeOff className="w-3 h-3 text-muted-foreground" />
          )}
          {layer.locked && <Lock className="w-3 h-3 text-muted-foreground" />}
        </div>
      </div>

      {/* Render children */}
      {layer.hasChildren && !layer.collapsed && (
        <div>
          {layer.children.map((child) => (
            <SortableLayerItem
              key={child.id}
              layer={child}
              selectedElementPath={selectedElementPath}
              onElementSelect={onElementSelect}
              onElementHover={onElementHover}
              onToggleCollapse={onToggleCollapse}
              overId={overId}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function LayersPanel({
  layers,
  selectedElementPath,
  onElementSelect,
  onElementHover,
  onReorder,
  readOnly = false,
}: LayersPanelProps) {
  const [collapsedElements, setCollapsedElements] = useState<Set<string>>(
    new Set(),
  );
  const [items, setItems] = useState<LayerElement[]>(layers);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Update items when layers prop changes
  useEffect(() => {
    setItems(layers);
  }, [layers]);

  const handleToggleCollapse = (elementId: string) => {
    setCollapsedElements((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(elementId)) {
        newSet.delete(elementId);
      } else {
        newSet.add(elementId);
      }
      return newSet;
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    console.log("event", event);
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    const newOverId = over ? (over.id as string) : null;
    setOverId(newOverId);

    // Trigger hover in iframe when dragging over element
    if (onElementHover && newOverId) {
      onElementHover(newOverId);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    // Clear hover in iframe when drag ends
    if (onElementHover) {
      onElementHover(null);
    }

    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });

      // Notify parent component about reorder
      if (onReorder) {
        onReorder(active.id as string, over.id as string);
      }
    }
  };

  // Apply collapsed state to layers
  const updateCollapsedState = (layers: LayerElement[]): LayerElement[] => {
    return layers.map((layer) => ({
      ...layer,
      collapsed: collapsedElements.has(layer.id),
      children: updateCollapsedState(layer.children),
    }));
  };

  const layersWithState = updateCollapsedState(items);

  if (!layers || layers.length === 0) {
    return (
      <div className="flex flex-col h-full bg-background overflow-hidden">
        {/* Header */}
        <div className="h-12 px-4 border-b border-border flex items-center gap-2 bg-background shrink-0">
          <Layers className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Layers</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <div className="rounded-full bg-primary/10 p-4 mb-4">
            <Layers className="w-12 h-12 text-primary" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            No Layers
          </h3>
          <p className="text-sm text-muted-foreground max-w-xs">
            Layers will appear here once the preview loads
          </p>
        </div>
      </div>
    );
  }

  const layerIds = layersWithState.map((layer) => layer.id);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col h-full bg-background overflow-hidden">
        {/* Header */}
        <div className="h-12 px-4 border-b border-border flex items-center justify-between bg-background shrink-0">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Layers</span>
          </div>
          <span className="text-xs text-muted-foreground">
            {layers.length} elements
          </span>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-auto">
          <div className="p-2">
            <SortableContext
              items={layerIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-0.5">
                {layersWithState.map((layer) => (
                  <SortableLayerItem
                    key={layer.id}
                    layer={layer}
                    selectedElementPath={selectedElementPath}
                    onElementSelect={onElementSelect}
                    onElementHover={onElementHover}
                    onToggleCollapse={handleToggleCollapse}
                    overId={overId}
                    readOnly={readOnly}
                  />
                ))}
              </div>
            </SortableContext>
          </div>
        </div>
      </div>
      <DragOverlay dropAnimation={null}>
        {activeId ? (
          <div className="bg-card shadow-lg rounded px-3 py-2 opacity-90 border border-border">
            <span className="text-sm font-medium text-foreground">
              {layersWithState.find((l) => l.id === activeId)?.name}
            </span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
